import { DEFAULT_COMPRESSION_MODE, getCompressionMode } from './presets.js';

export const DEFAULT_TIMEOUT = 120000;
export const MIN_RETRY_QUALITY = 30;
export const RETRY_QUALITY_STEP = 8;

/**
 * 规范化压缩参数。
 * @param {object} options 用户传入的压缩参数。
 * @param {boolean} [options.useOriginalWhenCompressedLarger=false] 压缩后体积不小于原图时是否允许回退原图。
 * @returns {object} 规范化后的压缩参数。
 */
export function normalizeCompressionOptions(options = {}) {
  const mode = getCompressionMode(options.mode || DEFAULT_COMPRESSION_MODE);
  const quality = normalizeQuality(
    options.quality ?? mode.quality,
    mode.quality
  );

  return {
    mode: mode.id,
    format: normalizeFormatOption(options.format),
    quality,
    maxWidth: normalizePositiveInteger(options.maxWidth),
    maxHeight: normalizePositiveInteger(options.maxHeight),
    customWidth: normalizePositiveInteger(options.customWidth),
    customHeight: normalizePositiveInteger(options.customHeight),
    maxSizeBytes: normalizeMaxSizeBytes(options.maxSizeMB),
    thresholdSizeBytes: normalizeMaxSizeBytes(options.thresholdSize),
    useOriginalWhenCompressedLarger:
      options.useOriginalWhenCompressedLarger === true,
    timeout: normalizeTimeout(options.timeout),
    concurrency: normalizePositiveInteger(options.concurrency) || 1,
    signal: isAbortSignal(options.signal) ? options.signal : null,
    consoleOptions: normalizeConsoleOptions(options.consoleOptions),
    createWorker:
      typeof options.createWorker === 'function' ? options.createWorker : null,
    workerOptions: createWorkerOptions(mode, quality, options)
  };
}

/**
 * 根据压缩模式和质量创建 worker 参数。
 * @param {object} mode 压缩模式配置。
 * @param {number} quality 压缩质量。
 * @param {object} options 用户传入的压缩参数。
 * @returns {object} worker 可接收的压缩参数。
 */
export function createWorkerOptions(mode, quality, options = {}) {
  return {
    format: normalizeFormatOption(options.format),
    quality,
    maxWidth: normalizePositiveInteger(options.maxWidth),
    maxHeight: normalizePositiveInteger(options.maxHeight),
    customWidth: normalizePositiveInteger(options.customWidth),
    customHeight: normalizePositiveInteger(options.customHeight),
    maxSizeBytes: normalizeMaxSizeBytes(options.maxSizeMB),
    thresholdSizeBytes: normalizeMaxSizeBytes(options.thresholdSize),
    forceNormalEncodeCompression: options.forceNormalEncodeCompression === true,
    webpEffort: normalizePositiveInteger(options.webpEffort) || mode.webpEffort,
    avifEffort: normalizePositiveInteger(options.avifEffort) || mode.avifEffort,
    pngLevel: normalizePositiveInteger(options.pngLevel) || mode.pngLevel,
    imageSmoothingQuality:
      options.imageSmoothingQuality || mode.imageSmoothingQuality
  };
}

/**
 * 判断压缩结果是否超过最大体积。
 * @param {object} options 规范化后的压缩参数。
 * @param {Blob} blob 压缩结果 Blob。
 * @returns {boolean} 超过最大体积时返回 true。
 */
export function exceedsMaxSize(options, blob) {
  // 未设置最大体积时不触发二次压缩。
  if (!options.maxSizeBytes) return false;

  return blob.size > options.maxSizeBytes;
}

/**
 * 规范化压缩质量。
 * @param {number} value 用户传入的质量。
 * @param {number} fallback 默认质量。
 * @returns {number} 1 到 100 之间的整数质量。
 */
export function normalizeQuality(value, fallback = 70) {
  const quality = Math.round(Number(value) || fallback);
  return Math.max(1, Math.min(100, quality));
}

/**
 * 规范化正整数参数。
 * @param {number|string} value 用户输入值。
 * @returns {number} 正整数，非法时返回 0。
 */
export function normalizePositiveInteger(value) {
  const numberValue = Math.floor(Number(value) || 0);

  // 小于等于 0 的值视为未设置。
  if (numberValue <= 0) return 0;

  return numberValue;
}

/**
 * 规范化输出格式。
 * @param {string} format 用户传入格式。
 * @returns {string} 合法格式或 auto。
 */
export function normalizeFormatOption(format) {
  const normalizedFormat = String(format || 'auto').toLowerCase();
  const validFormats = new Set(['auto', 'jpeg', 'png', 'webp', 'avif']);

  // 非法格式统一回退为自动格式。
  if (!validFormats.has(normalizedFormat)) return 'auto';

  return normalizedFormat;
}

/**
 * 将最大 MB 转为字节数。
 * @param {number|string} value 最大 MB。
 * @returns {number} 最大字节数，未设置时返回 0。
 */
export function normalizeMaxSizeBytes(value) {
  const sizeMB = Number(value) || 0;

  // 小于等于 0 的值视为不限制体积。
  if (sizeMB <= 0) return 0;

  return Math.floor(sizeMB * 1024 * 1024);
}

/**
 * 规范化 worker 超时时间。
 * @param {number|string} timeout 超时时间，单位毫秒。
 * @returns {number} 超时时间。
 */
export function normalizeTimeout(timeout) {
  const normalizedTimeout = Math.floor(Number(timeout) || DEFAULT_TIMEOUT);

  // 超时时间必须为正数。
  if (normalizedTimeout <= 0) return DEFAULT_TIMEOUT;

  return normalizedTimeout;
}

/**
 * 判断传入对象是否符合 AbortSignal 的最小接口。
 * @param {*} signal 用户传入的中断信号。
 * @returns {boolean} 是 AbortSignal 时返回 true。
 */
function isAbortSignal(signal) {
  // AbortSignal 至少需要支持 aborted 标记和事件监听。
  if (!signal || typeof signal.addEventListener !== 'function') return false;

  return typeof signal.aborted === 'boolean';
}

/**
 * 规范化压缩调试日志配置。
 * @param {object} consoleOptions 用户传入的日志配置。
 * @returns {object|null} 可用日志配置。
 */
function normalizeConsoleOptions(consoleOptions) {
  // 未传入调试配置时保持静默。
  if (!consoleOptions || typeof consoleOptions.console !== 'function') {
    return null;
  }

  return consoleOptions;
}
